import React from 'react';

import { UxContext } from '@client/context/UxContext';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import { RecipeSection } from '@ledium/types';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';

import SectionEditor from './SectionEditor';

export interface SectionFormRef {
    isFormValid: () => Promise<boolean>;
    submit: () => void;
}

interface RecipeEditorSectionEditorProps {
    selectedSection: RecipeSection | null;
    setSelectedSection: (section: RecipeSection | null) => void;
    formRef: React.RefObject<SectionFormRef | null>;
    onSaveSection: (sectionData: RecipeSection, options?: { recipeSave: false }) => void;
    onDeleteSection: (sectionData: RecipeSection) => void;
}

const RecipeEditorSectionEditor = ({ selectedSection, setSelectedSection, formRef, onSaveSection, onDeleteSection }: RecipeEditorSectionEditorProps) => {
    const {
        pageState: { formData }
    } = usePageStore();
    const { handleAlert } = React.useContext(UxContext)!;
    const [sectionIsDirty, setSectionIsDirty] = React.useState(false);

    async function saveSection(sectionData: RecipeSection) {
        const isFormValid = await formRef.current!.isFormValid();
        if (isFormValid) {
            onSaveSection(sectionData);
        } else {
            formRef.current!.submit();
            onSaveSection(sectionData, { recipeSave: false });
        }
    }

    function closeSection() {
        setSelectedSection(null);
    }

    function requestCloseSection() {
        if (sectionIsDirty) {
            handleAlert({
                title: i18n('discard_section_changes_title') as string,
                message: i18n('discard_section_changes_message') as string,
                okCallback: closeSection
            });
        } else {
            closeSection();
        }
    }

    return (
        <Dialog
            open={!!selectedSection}
            fullWidth
            maxWidth="md"
            disableEnforceFocus
            PaperProps={{
                style: {
                    height: 'calc(100vh - 100px)',
                    minHeight: '500px'
                }
            }}
        >
            <DialogTitle style={{ position: 'relative', paddingRight: '48px' }}>
                Section: {selectedSection?.name || i18n('new_section_temporary_name')}
                {selectedSection?.sectionColor?.background && (
                    <div
                        style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            display: 'inline-block',
                            border: '1px solid #ddd',
                            marginLeft: '10px',
                            background: selectedSection.sectionColor.background,
                            verticalAlign: 'middle'
                        }}
                    />
                )}
                <IconButton aria-label="close" onClick={requestCloseSection} style={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ padding: 0, overflow: 'auto' }}>
                {selectedSection ? (
                    <SectionEditor
                        section={selectedSection}
                        onCancel={closeSection}
                        onSave={saveSection}
                        onDelete={onDeleteSection}
                        onDirtyChange={setSectionIsDirty}
                        readOnly={formData.readOnly}
                    />
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export default RecipeEditorSectionEditor;
